// b14: båtens uppställning, som passar alla båtar. Den ersätter b13:s två fasta sidor.
//
//   - Motorerna, en lista: ett namn, en plats och en instans i NMEA 2000, som kan saknas. Platsen
//     är motorns namn i signalerna (motor.varvtal@mitten), så en båt har högst en motor per plats.
//     En generator utan NMEA 2000 har en uppskattad förbrukning i l/h.
//   - Tankarna, en lista: sorten enligt PGN 127505 (diesel, bensin, färskvatten och så vidare),
//     numret (instansen i 127505), ett namn och en volym. Tankens namn i signalerna är sorten och
//     numret: diesel för nummer 0, diesel2 för nummer 1 (b10).
//   - Matningen: vilka motorer som tar från vilka tankar med bränsle. Flera motorer kan ta från en
//     tank, och en motor kan ta från flera tankar.
//   - Grupperna: motorerna och tankarna som hänger ihop genom matningen. Räckvidden räknas per
//     grupp, och båtens räckvidd är den kortaste gruppens.
//
// b15: motorerna har en sort (inombordare, utombordare eller generator), och platsen ett nummer
// när flera delar sida (babord2, signaler.ts). Värmarna är förbrukare som inte driver båten: en
// förbrukning i l/h, en tank och en signal för när de går. Mallarna ligger i katalogen
// (@koll/katalog, BK39), och förslaget väljer den första båtmallen vars villkor stämmer med det som
// navet hör.

import { type Batmall, KATALOGEN, type Katalog, type Katalogmall } from "@koll/katalog";
import { BRANSLETANK } from "./fard.ts";
import { FORBRUKARE, type Forbrukarnyckel, MOTORER, type Motor } from "./signaler.ts";

/** Tankarnas sorter, som navet namnger dem ur canboats TANK_TYPE i PGN 127505. */
export const TANKSORTER = ["diesel", "bensin", "farskvatten", "gravatten", "svartvatten"] as const;

export type Tanksort = (typeof TANKSORTER)[number];

export const TANKSORTNAMN: Record<Tanksort, string> = {
  diesel: "Diesel",
  bensin: "Bensin",
  farskvatten: "Färskvatten",
  gravatten: "Gråvatten",
  svartvatten: "Svartvatten",
};

/** b15: motorns sort. En generator driver inte båten. */
export const MOTORSORTER = ["inombordare", "utombordare", "generator"] as const;

export type Motorsort = (typeof MOTORSORTER)[number];

export const MOTORSORTNAMN: Record<Motorsort, string> = {
  inombordare: "Inombordare",
  utombordare: "Utombordare",
  generator: "Generator",
};

const BASNAMN: Record<string, string> = {
  babord: "Babord",
  styrbord: "Styrbord",
  mitten: "Mitten",
  generator: "Generatorn",
};

/** Platsens namn: Babord, Babord 2, Mitten, Generatorn, Generator 2. */
export function motorplatsnamn(m: string): string {
  const x = /^([a-z]+?)([2-4]?)$/.exec(m);
  if (!x) return m;
  const bas = x[1] as string;
  if (!x[2]) return BASNAMN[bas] ?? m;
  return `${bas === "generator" ? "Generator" : (BASNAMN[bas] ?? bas)} ${x[2]}`;
}

export const MOTORPLATSNAMN: Record<Motor, string> = Object.fromEntries(
  MOTORER.map((m) => [m, motorplatsnamn(m)]),
) as Record<Motor, string>;

/** Platsens sida och nummer: babord2 är babord, nummer 2. En generators sida är generator. */
export function motorplats(m: string): { bas: string; nr: number } | null {
  const x = /^(babord|styrbord|mitten|generator)([2-4]?)$/.exec(m);
  return x ? { bas: x[1] as string, nr: x[2] ? Number(x[2]) : 1 } : null;
}

export interface Uppstallningsmotor {
  plats: Motor;
  /** b15: sorten. Saknas den i en uppställning från b14 är den inombordare, eller generator. */
  sort: Motorsort;
  namn: string;
  /** Instansen i NMEA 2000 (127488 och 127489), eller null utan NMEA 2000. */
  instans: number | null;
  /** En generator utan NMEA 2000: den uppskattade förbrukningen i l/h. Null: inte inställd. */
  lh: number | null;
}

/**
 * b15: en förbrukare som inte driver båten och inte är en motor: en värmare. Utan en signal
 * räknas den inte.
 */
export interface Uppstallningsforbrukare {
  nyckel: Forbrukarnyckel;
  namn: string;
  /** Förbrukningen i l/h, eller null. */
  lh: number | null;
  /** Tanken med bränsle som den tar från, eller null. */
  tank: string | null;
  /**
   * Signalen för när den går: en signal som katalogen känner till (till exempel generator.gar),
   * eller en entitet i Home Assistant ("ha:switch.varmaren"). Null: ingen.
   */
  signal: string | null;
  /**
   * b16: signalen härleds i stället ur temperaturen vid utblåset (signal är då null). Gäller också
   * ett hus, till exempel en luftvärmepump eller en kamin. Utan fältet: null.
   */
  utblas?: Utblas | null;
}

/**
 * b16: "Temperaturen vid utblåset". Förbrukaren räknas som igång när utblåset har varit minst
 * paGrader varmare än jämförelsen i paMinuter, och som avslagen när skillnaden har varit under
 * avGrader i avMinuter. Navet räknar, med värdena som det får.
 */
export interface Utblas {
  /** Givaren vid utblåset: navets id för en givare över Bluetooth, eller en entitet i Home Assistant. */
  givare: string;
  /** Jämförelsen: en temperatur på platsen, till exempel kabin.temp eller ute.temp. */
  jamforelse: string;
  paGrader: number;
  paMinuter: number;
  avGrader: number;
  avMinuter: number;
}

/** b16: förvalen för utblåset (rapporten §4). */
export const UTBLAS_FORVAL = { paGrader: 15, paMinuter: 2, avGrader: 8, avMinuter: 5 } as const;

/** Givaren vid utblåset: en givare över Bluetooth (navets id) eller en temperatur i Home Assistant. */
export const UTBLAS_GIVARE = /^([0-9A-F]{4}-[0-9a-f]{4}|ha:sensor\.[a-z0-9_]{1,120})$/;

/** Jämförelserna: temperaturerna inne och ute som en plats kan ha, eller en zon i huset. */
export const UTBLAS_JAMFORELSER = [
  "kabin.temp",
  "ute.temp",
  "hus.inne.temp",
  "hus.ute.temp",
  "motorrum.temp",
] as const;

/** Sant om nyckeln är en jämförelse som utblåset kan räknas mot. */
export function arJamforelse(nyckel: string): boolean {
  return (
    (UTBLAS_JAMFORELSER as readonly string[]).includes(nyckel) ||
    /^hus\.zon\.temp@[^@\p{Cc}]{1,40}$/u.test(nyckel)
  );
}

/** Kontrollerar utblåsets inställning. Returnerar ett fel, eller null. */
export function kontrolleraUtblas(v: unknown): string | null {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return "Utblåset saknas";
  const x = v as Record<string, unknown>;
  const tillatna = ["givare", "jamforelse", "paGrader", "paMinuter", "avGrader", "avMinuter"];
  if (Object.keys(x).some((k) => !tillatna.includes(k))) return "Okänt fält för utblåset";
  if (typeof x.givare !== "string" || !UTBLAS_GIVARE.test(x.givare)) {
    return "Välj givaren vid utblåset: en givare över Bluetooth, eller en temperatur i Home Assistant (ha:sensor.…)";
  }
  if (typeof x.jamforelse !== "string" || !arJamforelse(x.jamforelse)) {
    return "Välj jämförelsen: temperaturen i kabinen, inne eller ute";
  }
  const tal = (n: unknown, min: number, max: number) =>
    typeof n === "number" && Number.isFinite(n) && n >= min && n <= max;
  if (!tal(x.paGrader, 2, 100)) return "Gränsen för på ska vara 2–100 °C";
  if (!tal(x.avGrader, 1, 99)) return "Gränsen för av ska vara 1–99 °C";
  if ((x.avGrader as number) >= (x.paGrader as number)) {
    return "Gränsen för av ska vara lägre än gränsen för på";
  }
  if (!tal(x.paMinuter, 1, 60) || !Number.isInteger(x.paMinuter)) {
    return "Minuterna för på ska vara 1–60";
  }
  if (!tal(x.avMinuter, 1, 120) || !Number.isInteger(x.avMinuter)) {
    return "Minuterna för av ska vara 1–120";
  }
  return null;
}

export interface Uppstallningstank {
  /** Tankens namn i signalerna: diesel, diesel2, svartvatten. */
  instans: string;
  namn: string;
  /** Volymen som ägaren har ställt in, eller null (då gäller tankgivarens). */
  volymL: number | null;
}

export interface Matning {
  motor: Motor;
  tank: string;
}

/** Båtmallarnas namn ur katalogen: en_motor, tva_gemensam och de andra. */
export type Mallnamn = string;

/** Båtmallarna i katalogen, i den ordning som förslaget prövar dem. */
export function batmallar(k: Katalog = KATALOGEN): (Katalogmall & { bat: Batmall })[] {
  return k.mall.filter((m): m is Katalogmall & { bat: Batmall } => m.bat !== undefined);
}

/** b14:s mallar, och b15:s med tre och fyra utombordare, ur katalogen. */
export const MALLAR: readonly Mallnamn[] = batmallar().map((m) => m.id);

const mallnamn = (m: Katalogmall) =>
  typeof m.namn === "string" ? m.namn : (m.namn.batkoll ?? m.id);

export const MALLNAMN: Record<Mallnamn, string> = Object.fromEntries(
  batmallar().map((m) => [m.id, mallnamn(m)]),
);

export interface Uppstallning {
  motorer: Uppstallningsmotor[];
  tankar: Uppstallningstank[];
  matning: Matning[];
  /** b15: värmarna. */
  forbrukare: Uppstallningsforbrukare[];
  /** En segelbåt: ingen räckvidd med farten nu under segling (när ingen motor går). */
  segelbat: boolean;
  /** Mallen som uppställningen kom ifrån, eller null om ägaren har ändrat den. */
  mall: Mallnamn | null;
  /** Ägaren har bekräftat uppställningen. Annars är den förvalet. */
  bekraftad: boolean;
}

/** En grupp: motorerna och tankarna som hänger ihop genom matningen. */
export interface Grupp {
  namn: string;
  motorer: Motor[];
  tankar: string[];
}

/** Det som navet hör, för förslaget. */
export interface Hort {
  /** Motorernas instanser på NMEA 2000, i ordning. */
  motorinstanser: number[];
  /** Tankarna (tank.niva) som navet har hört, i signalernas namn. */
  tankar: string[];
  /** Navet hör en generator, till exempel generator.gar från MasterBus. */
  generator: boolean;
}

export interface Uppstallningsforslag {
  mall: Mallnamn;
  /** Förslaget i ord, med det som navet hör. */
  text: string;
  hort: Hort;
  uppstallning: Uppstallning;
}

/** Tankens namn i signalerna ur sorten och numret: diesel och 0 är diesel, och 1 diesel2. */
export function tankinstans(sort: Tanksort, nr: number): string {
  return nr === 0 ? sort : `${sort}${nr + 1}`;
}

/** Sorten och numret ur tankens namn i signalerna, eller null. */
export function tanksort(instans: string): { sort: Tanksort; nr: number } | null {
  const m = /^([a-z]+?)(\d{0,2})$/.exec(instans);
  if (!m || !(TANKSORTER as readonly string[]).includes(m[1] as string)) return null;
  const tal = m[2] ? Number(m[2]) : 1;
  if (m[2] && tal < 2) return null;
  return { sort: m[1] as Tanksort, nr: tal - 1 };
}

/** Ett namn för en tank som ägaren inte har namngett: "Diesel", "Diesel 2". */
export function tanknamnFor(instans: string): string {
  const s = tanksort(instans);
  if (!s) return instans;
  return s.nr === 0 ? TANKSORTNAMN[s.sort] : `${TANKSORTNAMN[s.sort]} ${s.nr + 1}`;
}

/** Tankarna i sorternas ordning och sedan numret: diesel, diesel2, bensin, färskvatten. */
export const sorteraTankar = (t: string[]) =>
  [...new Set(t)].sort((a, b) => {
    const x = tanksort(a);
    const y = tanksort(b);
    const ix = x ? TANKSORTER.indexOf(x.sort) : 99;
    const iy = y ? TANKSORTER.indexOf(y.sort) : 99;
    return ix - iy || (x?.nr ?? 0) - (y?.nr ?? 0);
  });

/** Motorerna i platsernas ordning (MOTORER). */
export const motorordning = (m: Motor) => MOTORER.indexOf(m);

/** Mallens motorer, ifyllda med det som navet hör. */
function fyll(b: Batmall, hort: Hort, bransle: string[], upptagna: number[]) {
  const tank = (i: number) => {
    const forsta = bransle[0] ?? "diesel";
    if (i === 0) return forsta;
    return bransle[i] ?? (forsta === "diesel" ? `diesel${i + 1}` : `${forsta}${i + 1}`);
  };
  const motorer: Uppstallningsmotor[] = [];
  const matning: Matning[] = [];
  for (const x of b.motorer) {
    let instans: number | null;
    if (x.instans === "ledig") {
      // Generatorns instans: den första motorn på bussen som ingen annan motor har, eller ingen
      // (MasterBus säger när den går).
      instans =
        hort.motorinstanser.find(
          (i) => ![...upptagna, ...motorer.map((m) => m.instans)].includes(i),
        ) ?? null;
    } else instans = x.instans === null ? null : (hort.motorinstanser[x.instans] ?? x.instans);
    motorer.push({ plats: x.plats as Motor, sort: x.sort, namn: x.namn, instans, lh: null });
    if (x.tank !== null) matning.push({ motor: x.plats as Motor, tank: tank(x.tank) });
  }
  const tankar: Uppstallningstank[] = b.tankar.map((namn, i) => ({
    instans: tank(i),
    namn,
    volymL: null,
  }));
  return { motorer, matning, tankar };
}

/** Mallen för motorerna när mallen med generator räknar dem ur det som navet hör (b14). */
function basFor(hort: Hort, bransle: string[]): string {
  if (hort.motorinstanser.length >= 2)
    return bransle.length >= 2 ? "tva_oberoende" : "tva_gemensam";
  return "en_motor";
}

/** En mall, ifylld med det som navet hör: instanserna och tankarna. */
export function mall(namn: Mallnamn, hort: Hort, k: Katalog = KATALOGEN): Uppstallning {
  const mallar = batmallar(k);
  const m = mallar.find((x) => x.id === namn);
  if (!m) throw new Error(`okänd mall: ${namn}`);
  const bransle = sorteraTankar(hort.tankar.filter((t) => BRANSLETANK.test(t)));
  const ovriga = sorteraTankar(hort.tankar.filter((t) => !BRANSLETANK.test(t) && tanksort(t)));
  let u: Uppstallning;
  if (m.bat.bas === "efter_hort") {
    const bas = mallar.find((x) => x.id === basFor(hort, bransle));
    if (!bas) throw new Error("mallen för motorerna saknas i katalogen");
    const b = fyll(bas.bat, hort, bransle, []);
    const tillagg = fyll(
      { ...m.bat, tankar: [] },
      hort,
      bransle,
      b.motorer.map((x) => x.instans).filter((x): x is number => x !== null),
    );
    u = {
      motorer: [...b.motorer, ...tillagg.motorer],
      tankar: b.tankar,
      matning: [...b.matning, ...tillagg.matning],
      forbrukare: [],
      segelbat: m.bat.segelbat,
      mall: namn,
      bekraftad: false,
    };
  } else {
    const b = fyll(m.bat, hort, bransle, []);
    u = { ...b, forbrukare: [], segelbat: m.bat.segelbat, mall: namn, bekraftad: false };
  }
  u.tankar.push(...ovriga.map((t) => ({ instans: t, namn: tanknamnFor(t), volymL: null })));
  return u;
}

/**
 * Mallen som passar det som navet hör: den första båtmallen i katalogen vars villkor stämmer.
 * Segelbåten föreslås aldrig: den syns inte på bussen.
 */
export function foreslaMall(hort: Hort, k: Katalog = KATALOGEN): Mallnamn {
  const tankar = hort.tankar.filter((t) => BRANSLETANK.test(t)).length;
  const n = hort.motorinstanser.length;
  for (const m of batmallar(k)) {
    const v = m.bat.villkor;
    if (!v) continue;
    if (v.generator !== undefined && v.generator !== hort.generator) continue;
    if (v.minstMotorer !== undefined && n < v.minstMotorer) continue;
    if (v.hogstMotorer !== undefined && n > v.hogstMotorer) continue;
    if (v.minstTankar !== undefined && tankar < v.minstTankar) continue;
    return m.id;
  }
  return "en_motor";
}

/** Namnen i en lista: "Babord", "Babord och styrbord", "Babord, styrbord och generatorn". */
export function lista(namn: string[]): string {
  const n = namn.map((x, i) => (i === 0 ? x : x.charAt(0).toLowerCase() + x.slice(1)));
  if (n.length <= 1) return n.join("");
  return `${n.slice(0, -1).join(", ")} och ${n.at(-1)}`;
}

/** Grupperna ur matningen. En motor utan matning är en egen grupp, utan tankar. */
export function grupper(u: Uppstallning): Grupp[] {
  const forelder = new Map<string, string>();
  const rot = (x: string): string => {
    let r = x;
    while (forelder.get(r) !== r) r = forelder.get(r) as string;
    forelder.set(x, r);
    return r;
  };
  const nod = (x: string) => {
    if (!forelder.has(x)) forelder.set(x, x);
  };
  for (const m of u.motorer) nod(`m:${m.plats}`);
  for (const r of u.matning) {
    if (!u.motorer.some((m) => m.plats === r.motor)) continue;
    nod(`t:${r.tank}`);
    const a = rot(`m:${r.motor}`);
    const b = rot(`t:${r.tank}`);
    if (a !== b) forelder.set(a, b);
  }
  const per = new Map<string, Grupp>();
  const ordning = [...u.motorer].sort((a, b) => motorordning(a.plats) - motorordning(b.plats));
  for (const m of ordning) {
    const r = rot(`m:${m.plats}`);
    let g = per.get(r);
    if (!g) {
      g = { namn: "", motorer: [], tankar: [] };
      per.set(r, g);
    }
    g.motorer.push(m.plats);
  }
  for (const r of u.matning) {
    const g = per.get(rot(`t:${r.tank}`));
    if (g && !g.tankar.includes(r.tank)) g.tankar.push(r.tank);
  }
  const ut = [...per.values()];
  for (const g of ut) {
    g.namn = lista(g.motorer.map((p) => u.motorer.find((m) => m.plats === p)?.namn ?? p));
    g.tankar = sorteraTankar(g.tankar);
  }
  return ut;
}

/** Motorerna som tar från tanken. */
export function motorerFranTank(u: Uppstallning, tank: string): Motor[] {
  return MOTORER.filter((m) => u.matning.some((r) => r.motor === m && r.tank === tank));
}

const NAMN = /^[^\p{Cc}]{1,40}$/u;

/** En signal för en förbrukare: en signal i katalogen, eller en entitet i Home Assistant. */
export const FORBRUKARSIGNAL = /^(ha:[a-z_]+\.[a-z0-9_]{1,120}|[a-z0-9_]+(\.[a-z0-9_]+)+)$/;

/** Sorten för en motor utan sort (b14): en generator på platsen generator, annars inombordare. */
export const sortFor = (plats: string): Motorsort =>
  /^generator[2-4]?$/.test(plats) ? "generator" : "inombordare";

/** Kontrollerar en uppställning från appen. Returnerar ett fel, eller null. */
export function kontrolleraUppstallning(u: unknown): string | null {
  if (typeof u !== "object" || u === null || Array.isArray(u)) return "Uppställningen saknas";
  const x = u as Record<string, unknown>;
  if (!Array.isArray(x.motorer) || !Array.isArray(x.tankar) || !Array.isArray(x.matning)) {
    return "Uppställningen ska ha motorerna, tankarna och matningen";
  }
  if (x.motorer.length > 8) return "För många motorer: högst 8";
  const platser = new Set<string>();
  const instanser = new Set<number>();
  for (const m of x.motorer as Record<string, unknown>[]) {
    if (typeof m !== "object" || m === null) return "En motor saknas";
    if (!(MOTORER as readonly string[]).includes(m.plats as string)) {
      return "Motorns plats ska vara babord, styrbord, mitten eller generator, med ett nummer 2–4 när flera delar plats";
    }
    if (platser.has(m.plats as string)) return "Två motorer har samma plats";
    platser.add(m.plats as string);
    const sort = m.sort === undefined ? sortFor(m.plats as string) : m.sort;
    if (!(MOTORSORTER as readonly string[]).includes(sort as string)) {
      return "Motorns sort ska vara inombordare, utombordare eller generator";
    }
    if ((sort === "generator") !== (m.plats as string).startsWith("generator")) {
      return "En generator har platsen generator, och bara en generator";
    }
    if (typeof m.namn !== "string" || !NAMN.test(m.namn.trim()))
      return "Motorns namn ska vara 1–40 tecken";
    if (m.instans !== null) {
      if (
        !Number.isInteger(m.instans) ||
        (m.instans as number) < 0 ||
        (m.instans as number) > 252
      ) {
        return "Motorns instans ska vara ett heltal mellan 0 och 252";
      }
      if (instanser.has(m.instans as number)) {
        return "Två motorer har samma instans: motorerna ska ha olika instanser";
      }
      instanser.add(m.instans as number);
    }
    if (m.lh !== null && m.lh !== undefined) {
      if (typeof m.lh !== "number" || !Number.isFinite(m.lh) || m.lh < 0.1 || m.lh > 100) {
        return "Förbrukningen ska vara mellan 0,1 och 100 l/h";
      }
    }
  }
  if (x.tankar.length > 16) return "För många tankar";
  const tankar = new Set<string>();
  for (const t of x.tankar as Record<string, unknown>[]) {
    if (
      typeof t !== "object" ||
      t === null ||
      typeof t.instans !== "string" ||
      !tanksort(t.instans)
    ) {
      return "Tankens sort ska vara diesel, bensin, färskvatten, gråvatten eller svartvatten";
    }
    if (tankar.has(t.instans)) return "Två tankar har samma sort och nummer";
    tankar.add(t.instans);
    if (typeof t.namn !== "string" || !NAMN.test(t.namn.trim()))
      return "Tankens namn ska vara 1–40 tecken";
    if (t.volymL !== null && t.volymL !== undefined) {
      if (
        typeof t.volymL !== "number" ||
        !Number.isFinite(t.volymL) ||
        t.volymL < 10 ||
        t.volymL > 20_000
      ) {
        return "Tankens volym ska vara mellan 10 och 20 000 liter";
      }
    }
  }
  const par = new Set<string>();
  for (const r of x.matning as Record<string, unknown>[]) {
    if (typeof r !== "object" || r === null) return "Matningen saknas";
    if (!platser.has(r.motor as string)) return "Matningen har en motor som inte finns";
    if (!tankar.has(r.tank as string)) return "Matningen har en tank som inte finns";
    if (!BRANSLETANK.test(r.tank as string)) return "En motor kan bara ta från en tank med bränsle";
    const k = `${r.motor}/${r.tank}`;
    if (par.has(k)) return "Matningen har samma motor och tank två gånger";
    par.add(k);
  }
  if (x.forbrukare !== undefined) {
    if (!Array.isArray(x.forbrukare) || x.forbrukare.length > FORBRUKARE.length) {
      return "För många värmare: högst 4";
    }
    const nycklar = new Set<string>();
    for (const f of x.forbrukare as Record<string, unknown>[]) {
      if (typeof f !== "object" || f === null) return "En värmare saknas";
      if (!(FORBRUKARE as readonly string[]).includes(f.nyckel as string)) {
        return "Värmarens nyckel ska vara varmare, varmare2, varmare3 eller varmare4";
      }
      if (nycklar.has(f.nyckel as string)) return "Två värmare har samma nyckel";
      nycklar.add(f.nyckel as string);
      if (typeof f.namn !== "string" || !NAMN.test(f.namn.trim()))
        return "Värmarens namn ska vara 1–40 tecken";
      if (f.lh !== null && f.lh !== undefined) {
        if (typeof f.lh !== "number" || !Number.isFinite(f.lh) || f.lh < 0.05 || f.lh > 100) {
          return "Värmarens förbrukning ska vara mellan 0,05 och 100 l/h";
        }
      }
      if (f.tank !== null && f.tank !== undefined) {
        if (typeof f.tank !== "string" || !tankar.has(f.tank) || !BRANSLETANK.test(f.tank)) {
          return "Värmaren tar från en tank med bränsle som finns i uppställningen";
        }
      }
      if (f.signal !== null && f.signal !== undefined) {
        if (typeof f.signal !== "string" || !FORBRUKARSIGNAL.test(f.signal)) {
          return "Värmarens signal ska vara en signal, till exempel generator.gar, eller en entitet i Home Assistant (ha:switch.varmaren)";
        }
      }
      if (f.utblas !== null && f.utblas !== undefined) {
        const ufel = kontrolleraUtblas(f.utblas);
        if (ufel) return ufel;
        if (f.signal !== null && f.signal !== undefined) {
          return "Värmaren har antingen en signal eller temperaturen vid utblåset, inte båda";
        }
      }
    }
  }
  if (x.segelbat !== undefined && typeof x.segelbat !== "boolean")
    return "Segelbåten ska vara ja eller nej";
  if (x.mall !== undefined && x.mall !== null && !MALLAR.includes(x.mall as string)) {
    return "Okänd mall";
  }
  return null;
}

/** En kontrollerad uppställning från appen, med städade namn. */
export function tolkaUppstallning(u: unknown): Uppstallning | string {
  const fel = kontrolleraUppstallning(u);
  if (fel) return fel;
  const x = u as Uppstallning;
  return {
    motorer: x.motorer.map((m) => ({
      plats: m.plats,
      sort: m.sort ?? sortFor(m.plats),
      namn: m.namn.trim(),
      instans: m.instans,
      lh: m.lh ?? null,
    })),
    tankar: x.tankar.map((t) => ({
      instans: t.instans,
      namn: t.namn.trim(),
      volymL: t.volymL ?? null,
    })),
    matning: x.matning.map((r) => ({ motor: r.motor, tank: r.tank })),
    forbrukare: (x.forbrukare ?? []).map((f) => ({
      nyckel: f.nyckel,
      namn: f.namn.trim(),
      lh: f.lh ?? null,
      tank: f.tank ?? null,
      signal: f.signal ?? null,
      utblas: f.utblas ? { ...f.utblas } : null,
    })),
    segelbat: x.segelbat === true,
    mall: x.mall ?? null,
    bekraftad: true,
  };
}

/** b16: kurvan för en värmare efter utblåset, de senaste 24 timmarna. */
export interface Utblaskurva {
  nyckel: Forbrukarnyckel;
  namn: string;
  /** Jämförelsen, till exempel kabin.temp. */
  jamforelse: string;
  /** Utblåsets temperatur och jämförelsens, i tidsordning. */
  utblas: { tid: number; varde: number }[];
  jamfort: { tid: number; varde: number }[];
  /** När värmaren räknades som igång (forbrukare.gar). Till är null när den går nu. */
  igang: { fran: number; till: number | null }[];
  paGrader: number;
  avGrader: number;
}
